/**
 * A component's props, described once: the validator checks views against it, the docs and the agent's JSON Schema
 * are generated from it, and an editor can build a field for each prop from it.
 *
 * Any prop that holds data can be a binding instead, `{ "$": "board.items" }`: the value is read from the view's data
 * when it renders. Child nodes and actions can't be bound.
 */

interface Base {
  /** What the prop is for, in a sentence: shown in the docs and to the agent. */
  doc: string
  optional?: boolean
  /** Used when the prop is left out (implies optional). */
  default?: unknown
}

export type Field =
  | (Base & { kind: 'string'; max?: number })
  | (Base & { kind: 'number'; min?: number; max?: number; integer?: boolean })
  | (Base & { kind: 'boolean' })
  | (Base & { kind: 'enum'; values: readonly (string | number)[] })
  | (Base & { kind: 'array'; of: Field; max?: number })
  | (Base & { kind: 'object'; fields: Fields })
  /** Any object: a table row, a data record. Bindings anywhere inside it are read too. */
  | (Base & { kind: 'record' })
  /** A string, number, boolean, or null: a value to show. */
  | (Base & { kind: 'value' })
  /** Anything: a list item of any shape. */
  | (Base & { kind: 'any' })
  /** One child node. */
  | (Base & { kind: 'node' })
  /** A list of child nodes. */
  | (Base & { kind: 'nodes' })
  /** What happens on click: `{ "open": url }` or `{ "ask": text }`. */
  | (Base & { kind: 'action' })

export type Fields = Record<string, Field>
export type FieldKind = Field['kind']

type Opts = Omit<Base, 'doc'>

/** Builders for props, e.g. `t.string('What it says', { max: 80 })`. */
export const t = {
  string: (doc: string, o: Opts & { max?: number } = {}): Field => ({ kind: 'string', doc, ...o }),
  number: (doc: string, o: Opts & { min?: number; max?: number; integer?: boolean } = {}): Field => ({
    kind: 'number',
    doc,
    ...o,
  }),
  boolean: (doc: string, o: Opts = {}): Field => ({ kind: 'boolean', doc, ...o }),
  enum: (values: readonly (string | number)[], doc: string, o: Opts = {}): Field => ({
    kind: 'enum',
    values,
    doc,
    ...o,
  }),
  array: (of: Field, doc: string, o: Opts & { max?: number } = {}): Field => ({ kind: 'array', of, doc, ...o }),
  object: (fields: Fields, doc: string, o: Opts = {}): Field => ({ kind: 'object', fields, doc, ...o }),
  record: (doc: string, o: Opts = {}): Field => ({ kind: 'record', doc, ...o }),
  value: (doc: string, o: Opts = {}): Field => ({ kind: 'value', doc, ...o }),
  any: (doc: string, o: Opts = {}): Field => ({ kind: 'any', doc, ...o }),
  node: (doc: string, o: Opts = {}): Field => ({ kind: 'node', doc, ...o }),
  nodes: (doc: string, o: Opts = {}): Field => ({ kind: 'nodes', doc, ...o }),
  action: (doc: string, o: Opts = {}): Field => ({ kind: 'action', doc, ...o }),
}

/** Whether a prop may be left out. */
export const isOptional = (f: Field) => !!f.optional || f.default !== undefined

/** Whether a prop can be a binding: everything but child nodes and actions. */
export const isBindable = (f: Field) => f.kind !== 'node' && f.kind !== 'nodes' && f.kind !== 'action'

// ---- bindings -------------------------------------------------------------------------------------------------------

/** `{ "$": "board.items.0.score" }`: a value read from the view's data (or `context`, or a `repeat`'s item). */
export interface Binding {
  $: string
}

const PATH = /^[A-Za-z_][\w-]*(\.[\w-]+)*$/

export function isBinding(value: unknown): value is Binding {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 1 &&
    typeof (value as Binding).$ === 'string'
  )
}

/** The first name in a binding's path: the data source, `context`, or a `repeat`'s `as`. */
export const bindingRoot = (b: Binding) => b.$.split('.')[0]!

/** Why a binding's path can't be read, or null. */
export function bindingIssue(b: Binding, scope: ReadonlySet<string>): string | null {
  if (!PATH.test(b.$))
    return `"${b.$}" isn't a path; paths are names and indexes joined by dots, like "board.items.0.score"`
  const root = bindingRoot(b)
  if (scope.has(root)) return null
  const known = [...scope].map((s) => `"${s}"`).join(', ')
  return `binds to "${b.$}", but nothing is called "${root}" here (${known ? `you can bind to ${known}` : 'the view has no data'})`
}

/** The value at a dot path (`items.0.score`; arrays also have `length`), or undefined. */
export function readPath(scope: Record<string, unknown>, path: string): unknown {
  let at: unknown = scope
  for (const key of path.split('.')) {
    if (at === null || at === undefined || typeof at !== 'object') return undefined
    if (!Object.hasOwn(at, key) && !(Array.isArray(at) && key === 'length')) return undefined
    at = (at as Record<string, unknown>)[key]
  }
  return at
}

// ---- checking -------------------------------------------------------------------------------------------------------

export interface Issue {
  /** Where, e.g. `body[2].children[0].value`. */
  path: string
  message: string
}

export interface CheckContext {
  issues: Issue[]
  /** What bindings can read. Null when checking resolved values (any binding left over is an issue). */
  scope: ReadonlySet<string> | null
  /** Checks a child node (the validator recurses through it). */
  node: (value: unknown, path: string, scope: ReadonlySet<string> | null) => void
}

const typeName = (v: unknown) =>
  v === null
    ? 'null'
    : Array.isArray(v)
      ? 'a list'
      : typeof v === 'object'
        ? 'an object'
        : `${typeof v} ${JSON.stringify(v).slice(0, 40)}`

/** The bindings anywhere inside a record (`{ "hits": { "$": "board.entries" } }`): each must read something here. */
function checkInnerBindings(value: unknown, path: string, ctx: CheckContext): void {
  if (isBinding(value)) {
    const why = ctx.scope ? bindingIssue(value, ctx.scope) : `binding "${value.$}" wasn't resolved`
    if (why) ctx.issues.push({ path, message: why })
    return
  }
  if (Array.isArray(value)) value.forEach((v, i) => checkInnerBindings(v, `${path}[${i}]`, ctx))
  else if (value && typeof value === 'object')
    for (const [k, v] of Object.entries(value)) checkInnerBindings(v, `${path}.${k}`, ctx)
}

/** Checks `value` against `field`, adding to `ctx.issues`. */
export function checkField(field: Field, value: unknown, path: string, ctx: CheckContext): void {
  const issue = (message: string): void => void ctx.issues.push({ path, message })
  if (isBinding(value)) {
    if (!isBindable(field))
      return issue(`can't be a binding (${field.kind === 'action' ? 'actions' : 'child nodes'} are written out)`)
    if (!ctx.scope) return issue(`binding "${value.$}" wasn't resolved`)
    const why = bindingIssue(value, ctx.scope)
    return void (why && issue(why))
  }
  switch (field.kind) {
    case 'string':
      if (typeof value !== 'string') return issue(`should be text, not ${typeName(value)}`)
      if (field.max !== undefined && value.length > field.max)
        issue(`is ${value.length} characters; keep it to ${field.max}`)
      return
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value))
        return issue(`should be a number, not ${typeName(value)}`)
      if (field.integer && !Number.isInteger(value)) issue('should be a whole number')
      if (field.min !== undefined && value < field.min) issue(`should be at least ${field.min}`)
      if (field.max !== undefined && value > field.max) issue(`should be at most ${field.max}`)
      return
    case 'boolean':
      if (typeof value !== 'boolean') issue(`should be true or false, not ${typeName(value)}`)
      return
    case 'enum':
      if (!field.values.includes(value as string))
        issue(`should be one of ${field.values.map((v) => JSON.stringify(v)).join(', ')}, not ${typeName(value)}`)
      return
    case 'array':
      if (!Array.isArray(value)) return issue(`should be a list, not ${typeName(value)}`)
      if (field.max !== undefined && value.length > field.max) issue(`has ${value.length} items; at most ${field.max}`)
      value.forEach((item, i) => checkField(field.of, item, `${path}[${i}]`, ctx))
      return
    case 'object':
      if (!value || typeof value !== 'object' || Array.isArray(value))
        return issue(`should be an object with ${Object.keys(field.fields).join(', ')}`)
      checkFields(field.fields, value as Record<string, unknown>, path, ctx)
      return
    case 'record':
      if (!value || typeof value !== 'object' || Array.isArray(value))
        return issue(`should be an object, not ${typeName(value)}`)
      return checkInnerBindings(value, path, ctx)
    case 'value':
      if (value !== null && !['string', 'number', 'boolean'].includes(typeof value))
        issue(`should be text, a number, true/false, or null; not ${typeName(value)}`)
      return
    case 'any':
      return
    case 'node':
      return ctx.node(value, path, ctx.scope)
    case 'nodes':
      if (!Array.isArray(value)) return issue(`should be a list of nodes, not ${typeName(value)}`)
      value.forEach((n, i) => ctx.node(n, `${path}[${i}]`, ctx.scope))
      return
    case 'action':
      return checkAction(value, path, ctx.issues)
  }
}

/** Checks an object's props: unknown ones, missing ones, and each value. */
export function checkFields(
  fields: Fields,
  value: Record<string, unknown>,
  path: string,
  ctx: CheckContext,
  reserved: readonly string[] = []
): void {
  const at = (key: string) => (path ? `${path}.${key}` : key)
  for (const key of Object.keys(value)) {
    if (reserved.includes(key)) continue
    if (!Object.hasOwn(fields, key)) {
      ctx.issues.push({
        path: at(key),
        message: `isn't a prop here; the props are ${Object.keys(fields).join(', ') || '(none)'}`,
      })
    }
  }
  for (const [key, field] of Object.entries(fields)) {
    const v = value[key]
    if (v === undefined) {
      if (!isOptional(field)) ctx.issues.push({ path: at(key), message: `is required: ${field.doc}` })
      continue
    }
    checkField(field, v, at(key), ctx)
  }
}

// ---- actions --------------------------------------------------------------------------------------------------------

/**
 * What a button does. `open`: go to a link (a path on this site like `/policies`, or an https URL, which opens in a new tab). `ask`: send the player's agent a message, as if the player typed it.
 */
export type Action = { open: string } | { ask: string }

// Browsers strip tabs and newlines from an address and read a backslash as a slash, so `/<tab>/evil.example` goes to another
// site. These are parsed the way a browser will, rather than matched.
const unsafeInAddress = (value: string) =>
  value.includes('\\') ||
  [...value].some((character) => character.charCodeAt(0) <= 0x20 || character.charCodeAt(0) === 0x7f)
const SITE = 'https://site.invalid'

const isSitePath = (value: string) => value.startsWith('/') && new URL(value, SITE).origin === SITE

/** Where a view may send someone, and only ever on a click: a path on this site or an https link. */
export function isSafeLink(value: string): boolean {
  if (unsafeInAddress(value) || !URL.canParse(value, SITE)) return false

  if (isSitePath(value)) return true

  const url = new URL(value, SITE)
  return value.startsWith('https://') && url.protocol === 'https:' && !url.username && !url.password
}

/** A picture loads by itself, so it can't come from another site: its address could carry what the agent read. */
export function isSafeImage(value: string): boolean {
  if (unsafeInAddress(value) || !URL.canParse(value, SITE)) return false

  return isSitePath(value) || /^data:image\/(?:png|jpeg|gif|webp);base64,/.test(value)
}

export function checkAction(value: unknown, path: string, issues: Issue[]): void {
  const a = value as Record<string, unknown> | null
  const keys = a && typeof a === 'object' && !Array.isArray(a) ? Object.keys(a) : []
  const ok =
    keys.length === 1 &&
    (keys[0] === 'open' || keys[0] === 'ask') &&
    typeof a![keys[0]] === 'string' &&
    (a![keys[0]] as string).trim()
  if (!ok)
    return void issues.push({ path, message: 'should be { "open": "<link>" } or { "ask": "<message for the agent>" }' })
  if ('open' in a! && !isSafeLink(a.open as string))
    issues.push({ path: `${path}.open`, message: 'should be a site path starting "/" or an https:// link' })
}

// ---- JSON Schema ----------------------------------------------------------------------------------------------------

export type JsonSchema = Record<string, unknown>

/** The JSON Schema of a prop: bindable props also accept `{ "$": path }` (`#/$defs/binding`). */
export function fieldSchema(field: Field): JsonSchema {
  const base = baseSchema(field)
  const described = (s: JsonSchema): JsonSchema => ({
    ...s,
    description: field.doc,
    ...(field.default !== undefined ? { default: field.default } : {}),
  })
  return isBindable(field) ? described({ anyOf: [base, { $ref: '#/$defs/binding' }] }) : described(base)
}

function baseSchema(field: Field): JsonSchema {
  switch (field.kind) {
    case 'string':
      return { type: 'string', ...(field.max !== undefined ? { maxLength: field.max } : {}) }
    case 'number':
      return {
        type: field.integer ? 'integer' : 'number',
        ...(field.min !== undefined ? { minimum: field.min } : {}),
        ...(field.max !== undefined ? { maximum: field.max } : {}),
      }
    case 'boolean':
      return { type: 'boolean' }
    case 'enum':
      return { enum: [...field.values] }
    case 'array':
      return {
        type: 'array',
        items: fieldSchema(field.of),
        ...(field.max !== undefined ? { maxItems: field.max } : {}),
      }
    case 'object':
      return objectSchema(field.fields)
    case 'record':
      return { type: 'object' }
    case 'value':
      return { type: ['string', 'number', 'boolean', 'null'] }
    case 'any':
      return {}
    case 'node':
      return { $ref: '#/$defs/node' }
    case 'nodes':
      return { type: 'array', items: { $ref: '#/$defs/node' } }
    case 'action':
      return { $ref: '#/$defs/action' }
  }
}

export function objectSchema(fields: Fields, extra: Record<string, JsonSchema> = {}): JsonSchema {
  const properties: Record<string, JsonSchema> = { ...extra }
  for (const [k, f] of Object.entries(fields)) properties[k] = fieldSchema(f)
  const required = [
    ...Object.keys(extra).filter((k) => k === 'type'),
    ...Object.entries(fields)
      .filter(([, f]) => !isOptional(f))
      .map(([k]) => k),
  ]
  return { type: 'object', properties, required, additionalProperties: false }
}

// ---- docs -----------------------------------------------------------------------------------------------------------

/** A prop's type in a few words, for the docs: `text`, `"down" | "across"`, `list of objects`, `nodes`. */
export function typeLabel(field: Field): string {
  switch (field.kind) {
    case 'string':
      return 'text'
    case 'number':
      return field.integer ? 'integer' : 'number'
    case 'boolean':
      return 'boolean'
    case 'enum':
      return field.values.map((v) => JSON.stringify(v)).join(' \\| ')
    case 'array':
      return `list of ${field.of.kind === 'object' ? 'objects' : typeLabel(field.of)}`
    case 'object':
      return 'object'
    case 'record':
      return 'object (any fields)'
    case 'value':
      return 'text, number, boolean, or null'
    case 'any':
      return 'any'
    case 'node':
      return 'node'
    case 'nodes':
      return 'list of nodes'
    case 'action':
      return 'action'
  }
}
